/**
 * usePedidoData Hook
 * 
 * Manages fetching and refreshing pedidos and comandas for the Sales component.
 * Handles different modes: mesa, group, and delivery/pickup pedidos.
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getActivePedidosByMesaId, getActivePedidosByPedidoGroupId } from '../../../../../../../../crud/pedidos_api/pedidos';
import { GetComandasByMesaId, GetComandasByPedidoId } from '../../../../../../../../crud/pedidos_api/comandas';
import { VIEW_MODES, type ViewMode } from '../utils';

interface UsePedidoDataProps {
  selectedMesa: any;
  selectedGroup?: any;
  selectedPedido?: any;
  isPedidosMode: boolean;
  setPedidos: (pedidos: any[]) => void;
  setSelections: (selections: any[]) => void;
  setViewMode: (mode: ViewMode) => void;
}

interface UsePedidoDataReturn {
  comandas: any[];
  setComandas: React.Dispatch<React.SetStateAction<any[]>>;
  loading: boolean;
  refreshPedidos: () => void;
  refreshComandas: () => void;
}

/**
 * Custom hook to manage pedido and comanda data fetching
 */
export const usePedidoData = ({
  selectedMesa,
  selectedGroup,
  selectedPedido,
  isPedidosMode,
  setPedidos,
  setSelections,
  setViewMode,
}: UsePedidoDataProps): UsePedidoDataReturn => {
  const [comandas, setComandas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Refresh pedidos for the current mesa, group, or selected pedido
  const refreshPedidos = useCallback(() => {
    if (isPedidosMode && selectedPedido) {
      // For delivery/pickup pedidos, treat as a single-pedido array
      setPedidos([selectedPedido]);
      setSelections(selectedPedido.selections || []);
      return;
    }
    
    if (selectedGroup) {
      // Fetch pedidos by group ID
      getActivePedidosByPedidoGroupId(selectedGroup.pedido_group_id)
        .then((response) => {
          const data = response.data.reverse();
          setPedidos(data);
          
          // If no active pedidos after refresh, switch to order taker
          if (data.length === 0) {
            setViewMode(VIEW_MODES.ORDER);
            setSelections([]);
          }
          
          const pedidosSelections = data.map((pedido: any) => pedido.selections);
          const allSelections = pedidosSelections.flat();
          setSelections(allSelections);
        })
        .catch((error) => {
          toast.error('Error actualizando los pedidos del grupo');
        });
      return;
    }
    
    if (!selectedMesa) return;
    
    getActivePedidosByMesaId(selectedMesa.id)
      .then((response) => {
        const data = response.data.reverse();
        setPedidos(data);
        
        // If no active pedidos after refresh, switch to order taker
        if (data.length === 0) {
          setSelections([]);
          setViewMode(VIEW_MODES.ORDER);
          return;
        }
        
        const pedidosSelections = data.map((pedido: any) => pedido.selections);
        const allSelections = pedidosSelections.flat();
        setSelections(allSelections);
      })
      .catch((error) => {
        toast.error('Error actualizando los pedidos de la mesa');
      });
  }, [selectedMesa, selectedGroup, setPedidos, isPedidosMode, selectedPedido, setSelections, setViewMode]);

  // Refresh comandas for the current mesa or selected pedido
  const refreshComandas = useCallback(() => {
    if (isPedidosMode && selectedPedido) {
      GetComandasByPedidoId(selectedPedido.pedido_id)
        .then((response: any) => {
          setComandas(response.data.reverse());
        })
        .catch((error: any) => {
          toast.error('Error actualizando las comandas del pedido');
        });
      return;
    }
    
    if (!selectedMesa) return;
    
    GetComandasByMesaId(selectedMesa.id)
      .then((response: any) => {
        setComandas(response.data.reverse());
      })
      .catch((error: any) => {
        toast.error('Error actualizando las comandas de la mesa');
      });
  }, [selectedMesa, isPedidosMode, selectedPedido]);

  // Initial data load when selection changes
  useEffect(() => {
    // Handle pedidos mode
    if (isPedidosMode && selectedPedido) {
      setLoading(true);
      setViewMode(VIEW_MODES.MESA);
      
      // Set the pedido as the active pedido
      setPedidos([selectedPedido]);
      setSelections(selectedPedido.selections || []);
      
      // Fetch comandas for this pedido
      GetComandasByPedidoId(selectedPedido.pedido_id)
        .then((response: any) => {
          setComandas(response.data.reverse());
          setLoading(false);
        })
        .catch((error: any) => {
          toast.error('Error obteniendo las comandas del pedido');
          setLoading(false);
        });
      
      return;
    }
    
    // Handle pedidos mode with no selection - show create form
    if (isPedidosMode && !selectedPedido) {
      setLoading(false);
      setSelections([]);
      setPedidos([]);
      setComandas([]);
      setViewMode(VIEW_MODES.CREATE_PEDIDO);
      return;
    }

    // Handle pedido groups
    if (selectedGroup) {
      setLoading(true);
      setViewMode(VIEW_MODES.MESA);

      // Fetch active pedidos for this group
      getActivePedidosByPedidoGroupId(selectedGroup.pedido_group_id)
        .then((response) => {
          const data = response.data.reverse();
          setPedidos(data);
          if (data.length === 0) {
            setSelections([]);
            setLoading(false);
            // If no pedidos, automatically show order taker
            setViewMode(VIEW_MODES.ORDER);
            return;
          }
          const pedidosSelections = data.map((pedido: any) => pedido.selections);
          const allSelections = pedidosSelections.flat();
          setSelections(allSelections);
          setLoading(false);
        })
        .catch((error) => {
          toast.error('Error obteniendo los pedidos del grupo');
          setLoading(false);
        });
      
      return;
    }

    // Handle no selection
    if (!selectedMesa) {
      console.log('[usePedidoData] No selectedMesa, clearing state');
      setLoading(false);
      setSelections([]);
      setPedidos([]);
      setComandas([]);
      return;
    }

    console.log('[usePedidoData] selectedMesa detected:', {
      id: selectedMesa.id,
      used: selectedMesa.used,
      charged: selectedMesa.charged
    });

    if (selectedMesa.used) {
      console.log('[usePedidoData] Mesa is used, setting viewMode to MESA');
      setLoading(true);
      setViewMode(VIEW_MODES.MESA);

      // Fetch active pedidos
      getActivePedidosByMesaId(selectedMesa.id)
        .then((response) => {
          const data = response.data.reverse();
          setPedidos(data);
          if (data.length === 0) {
            setSelections([]);
            setLoading(false);
            // If no pedidos, automatically show order taker
            setViewMode(VIEW_MODES.ORDER);
            return;
          }
          const pedidosSelections = data.map((pedido: any) => pedido.selections);
          const allSelections = pedidosSelections.flat();
          setSelections(allSelections);
          setLoading(false);
        })
        .catch((error) => {
          toast.error('Error obteniendo los pedidos de la mesa');
          setLoading(false);
        });

      // Fetch comandas
      GetComandasByMesaId(selectedMesa.id)
        .then((response: any) => {
          setComandas(response.data.reverse());
        })
        .catch((error: any) => {
          toast.error('Error obteniendo las comandas de la mesa');
        });
    } else {
      // Mesa not used - show order taker immediately
      setSelections([]);
      setPedidos([]);
      setComandas([]);
      setLoading(false);
      setViewMode(VIEW_MODES.ORDER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMesa?.id, selectedMesa?.used, selectedGroup?.pedido_group_id, selectedPedido?.pedido_id, isPedidosMode]); // Use specific properties to prevent re-running when only 'charged' changes

  return {
    comandas,
    setComandas,
    loading,
    refreshPedidos,
    refreshComandas,
  };
};
